'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type TimelineOrientation = 'vertical' | 'horizontal';
export type TimelinePosition = 'left' | 'right' | 'alternate';
export type TimelineVariant = 'default' | 'outlined' | 'filled';
export type TimelineSize = 'sm' | 'md' | 'lg';

export interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  date?: Date | string;
  icon?: React.ReactNode;
  color?: string;
  status?: 'completed' | 'current' | 'pending';
  metadata?: Record<string, unknown>;
}

export interface TimelineContextValue {
  orientation: TimelineOrientation;
  position: TimelinePosition;
  variant: TimelineVariant;
  size: TimelineSize;
}

const TimelineContext = React.createContext<TimelineContextValue | null>(null);

function useTimelineContext() {
  const context = React.useContext(TimelineContext);
  if (!context) {
    throw new Error('Timeline components must be used within a Timeline');
  }
  return context;
}

// ============================================================================
// Timeline
// ============================================================================

export interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: TimelineOrientation;
  position?: TimelinePosition;
  variant?: TimelineVariant;
  size?: TimelineSize;
  children: React.ReactNode;
}

export function Timeline({
  orientation = 'vertical',
  position = 'left',
  variant = 'default',
  size = 'md',
  children,
  className,
  ...props
}: TimelineProps) {
  const contextValue: TimelineContextValue = {
    orientation,
    position,
    variant,
    size,
  };

  return (
    <TimelineContext.Provider value={contextValue}>
      <div
        className={clsx(
          'timeline',
          orientation === 'horizontal' ? 'flex flex-row' : 'flex flex-col',
          className
        )}
        role="list"
        {...props}
      >
        {children}
      </div>
    </TimelineContext.Provider>
  );
}

// ============================================================================
// TimelineItem
// ============================================================================

export interface TimelineItemProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: 'left' | 'right';
  children: React.ReactNode;
}

export function TimelineItem({
  position: itemPosition,
  children,
  className,
  ...props
}: TimelineItemProps) {
  const { orientation, position: timelinePosition, size } = useTimelineContext();

  const effectivePosition = itemPosition || timelinePosition;
  const isHorizontal = orientation === 'horizontal';

  const sizeClasses = {
    sm: 'min-w-[120px]',
    md: 'min-w-[160px]',
    lg: 'min-w-[200px]',
  };

  return (
    <div
      className={clsx(
        'timeline-item relative',
        isHorizontal ? 'flex flex-col items-center' : 'flex',
        effectivePosition === 'right' && !isHorizontal && 'flex-row-reverse',
        isHorizontal && sizeClasses[size],
        className
      )}
      role="listitem"
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TimelineConnector
// ============================================================================

export interface TimelineConnectorProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
}

export function TimelineConnector({
  active = false,
  className,
  ...props
}: TimelineConnectorProps) {
  const { orientation } = useTimelineContext();
  const isHorizontal = orientation === 'horizontal';

  return (
    <div
      className={clsx(
        'timeline-connector',
        isHorizontal ? 'h-0.5 flex-1 mx-1' : 'w-0.5 flex-1 my-1',
        active ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700',
        className
      )}
      aria-hidden="true"
      {...props}
    />
  );
}

// ============================================================================
// TimelineDot
// ============================================================================

export interface TimelineDotProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: string;
  variant?: 'filled' | 'outlined';
  icon?: React.ReactNode;
  status?: 'completed' | 'current' | 'pending';
}

export function TimelineDot({
  color,
  variant = 'filled',
  icon,
  status,
  className,
  ...props
}: TimelineDotProps) {
  const { size } = useTimelineContext();

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const iconSizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const statusColors = {
    completed: 'bg-green-500',
    current: 'bg-blue-500',
    pending: 'bg-gray-300 dark:bg-gray-600',
  };

  const backgroundColor = status
    ? statusColors[status]
    : color || (variant === 'filled' ? 'bg-blue-500' : 'bg-transparent');

  if (icon) {
    return (
      <div
        className={clsx(
          'timeline-dot flex items-center justify-center rounded-full',
          iconSizeClasses[size],
          variant === 'outlined' && 'border-2 border-current',
          !variant && backgroundColor,
          className
        )}
        style={color && !status ? { backgroundColor: color } : undefined}
        {...props}
      >
        {icon}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'timeline-dot rounded-full',
        sizeClasses[size],
        variant === 'outlined' ? 'border-2 border-blue-500 bg-white dark:bg-gray-900' : backgroundColor,
        className
      )}
      style={color && !status ? { backgroundColor: color } : undefined}
      {...props}
    />
  );
}

// ============================================================================
// TimelineContent
// ============================================================================

export interface TimelineContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function TimelineContent({
  children,
  className,
  ...props
}: TimelineContentProps) {
  const { size } = useTimelineContext();

  const paddingClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
  };

  return (
    <div
      className={clsx(
        'timeline-content flex-1',
        paddingClasses[size],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TimelineSeparator
// ============================================================================

export interface TimelineSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function TimelineSeparator({
  children,
  className,
  ...props
}: TimelineSeparatorProps) {
  const { orientation } = useTimelineContext();
  const isHorizontal = orientation === 'horizontal';

  return (
    <div
      className={clsx(
        'timeline-separator flex items-center',
        isHorizontal ? 'flex-row' : 'flex-col',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TimelineOppositeContent
// ============================================================================

export interface TimelineOppositeContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function TimelineOppositeContent({
  children,
  className,
  ...props
}: TimelineOppositeContentProps) {
  const { size } = useTimelineContext();

  const paddingClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
  };

  return (
    <div
      className={clsx(
        'timeline-opposite-content flex-1 text-right',
        paddingClasses[size],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// HistoryList - Audit trail / activity history
// ============================================================================

export interface HistoryEntry {
  id: string;
  action: string;
  description?: string;
  user?: {
    name: string;
    avatar?: string;
  };
  timestamp: Date | string;
  changes?: {
    field: string;
    from?: string;
    to?: string;
  }[];
}

export interface HistoryListProps extends React.HTMLAttributes<HTMLDivElement> {
  entries: HistoryEntry[];
  showChanges?: boolean;
  dateFormat?: (date: Date | string) => string;
}

export function HistoryList({
  entries,
  showChanges = true,
  dateFormat = defaultDateFormat,
  className,
  ...props
}: HistoryListProps) {
  return (
    <div
      className={clsx('history-list space-y-4', className)}
      role="log"
      aria-label="History"
      {...props}
    >
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="history-entry flex gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
        >
          {entry.user?.avatar ? (
            <img
              src={entry.user.avatar}
              alt={entry.user.name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-xs font-medium">
                {entry.user?.name?.charAt(0) || '?'}
              </span>
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="font-medium">{entry.action}</span>
              <time className="text-sm text-gray-500 dark:text-gray-400" dateTime={String(entry.timestamp)}>
                {dateFormat(entry.timestamp)}
              </time>
            </div>
            {entry.user && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                by {entry.user.name}
              </p>
            )}
            {entry.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {entry.description}
              </p>
            )}
            {showChanges && entry.changes && entry.changes.length > 0 && (
              <div className="mt-2 space-y-1">
                {entry.changes.map((change, idx) => (
                  <div
                    key={idx}
                    className="text-xs text-gray-500 dark:text-gray-400"
                  >
                    <span className="font-medium">{change.field}</span>:{' '}
                    {change.from && (
                      <>
                        <span className="line-through">{change.from}</span>
                        {' → '}
                      </>
                    )}
                    <span>{change.to}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// ActivityFeed - Social-style activity stream
// ============================================================================

export interface ActivityItem {
  id: string;
  type: 'comment' | 'update' | 'create' | 'delete' | 'assign' | 'mention' | 'custom';
  actor: {
    name: string;
    avatar?: string;
  };
  target?: string;
  content?: string;
  timestamp: Date | string;
  icon?: React.ReactNode;
  link?: string;
}

export interface ActivityFeedProps extends React.HTMLAttributes<HTMLDivElement> {
  activities: ActivityItem[];
  onActivityClick?: (activity: ActivityItem) => void;
  dateFormat?: (date: Date | string) => string;
  emptyMessage?: string;
}

export function ActivityFeed({
  activities,
  onActivityClick,
  dateFormat = defaultDateFormat,
  emptyMessage = 'No activity yet',
  className,
  ...props
}: ActivityFeedProps) {
  if (activities.length === 0) {
    return (
      <div className={clsx('activity-feed text-center py-8 text-gray-500 dark:text-gray-400', className)} {...props}>
        {emptyMessage}
      </div>
    );
  }

  const getActivityIcon = (type: ActivityItem['type']) => {
    const icons: Record<ActivityItem['type'], string> = {
      comment: '💬',
      update: '✏️',
      create: '➕',
      delete: '🗑️',
      assign: '👤',
      mention: '@',
      custom: '•',
    };
    return icons[type];
  };

  const getActivityMessage = (activity: ActivityItem) => {
    const messages: Record<ActivityItem['type'], string> = {
      comment: 'commented on',
      update: 'updated',
      create: 'created',
      delete: 'deleted',
      assign: 'was assigned to',
      mention: 'mentioned in',
      custom: '',
    };
    return messages[activity.type];
  };

  return (
    <div
      className={clsx('activity-feed divide-y divide-gray-200 dark:divide-gray-700', className)}
      role="feed"
      aria-label="Activity feed"
      {...props}
    >
      {activities.map((activity) => (
        <article
          key={activity.id}
          className={clsx(
            'activity-item flex gap-3 py-3',
            onActivityClick && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800'
          )}
          onClick={() => onActivityClick?.(activity)}
          aria-label={`${activity.actor.name} ${getActivityMessage(activity)} ${activity.target || ''}`}
        >
          <div className="flex-shrink-0">
            {activity.actor.avatar ? (
              <img
                src={activity.actor.avatar}
                alt={activity.actor.name}
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-sm">{activity.icon || getActivityIcon(activity.type)}</span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-medium">{activity.actor.name}</span>
              {' '}
              {getActivityMessage(activity)}
              {activity.target && (
                <>
                  {' '}
                  <span className="font-medium">{activity.target}</span>
                </>
              )}
            </p>
            {activity.content && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                {activity.content}
              </p>
            )}
            <time className="text-xs text-gray-500 mt-1 block dark:text-gray-400" dateTime={String(activity.timestamp)}>
              {dateFormat(activity.timestamp)}
            </time>
          </div>
        </article>
      ))}
    </div>
  );
}

// ============================================================================
// ChangeLog - Version/release changelog
// ============================================================================

export interface ChangeLogEntry {
  version: string;
  date: Date | string;
  title?: string;
  changes: {
    type: 'added' | 'changed' | 'deprecated' | 'removed' | 'fixed' | 'security';
    description: string;
  }[];
}

export interface ChangeLogProps extends React.HTMLAttributes<HTMLDivElement> {
  entries: ChangeLogEntry[];
  showTypes?: boolean;
  dateFormat?: (date: Date | string) => string;
}

export function ChangeLog({
  entries,
  showTypes = true,
  dateFormat = defaultDateFormat,
  className,
  ...props
}: ChangeLogProps) {
  const typeLabels: Record<ChangeLogEntry['changes'][0]['type'], string> = {
    added: 'Added',
    changed: 'Changed',
    deprecated: 'Deprecated',
    removed: 'Removed',
    fixed: 'Fixed',
    security: 'Security',
  };

  const typeColors: Record<ChangeLogEntry['changes'][0]['type'], string> = {
    added: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    changed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    deprecated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    removed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    fixed: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    security: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };

  return (
    <div
      className={clsx('changelog space-y-8', className)}
      {...props}
    >
      {entries.map((entry) => (
        <article key={entry.version} className="changelog-entry">
          <header className="flex items-baseline gap-3 mb-3">
            <h3 className="text-xl font-bold">{entry.version}</h3>
            <time className="text-sm text-gray-500 dark:text-gray-400" dateTime={String(entry.date)}>
              {dateFormat(entry.date)}
            </time>
          </header>
          {entry.title && (
            <p className="text-gray-600 dark:text-gray-400 mb-3">{entry.title}</p>
          )}
          <ul className="space-y-2">
            {entry.changes.map((change, idx) => (
              <li key={idx} className="flex items-start gap-2">
                {showTypes && (
                  <span
                    className={clsx(
                      'text-xs font-medium px-2 py-0.5 rounded',
                      typeColors[change.type]
                    )}
                  >
                    {typeLabels[change.type]}
                  </span>
                )}
                <span className="text-sm">{change.description}</span>
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

// ============================================================================
// VerticalTimeline - Vertical orientation timeline preset
// ============================================================================

export interface VerticalTimelineProps extends Omit<TimelineProps, 'orientation'> {
  items: TimelineItem[];
  renderItem?: (item: TimelineItem, index: number) => React.ReactNode;
  dateFormat?: (date: Date | string) => string;
}

export function VerticalTimeline({
  items,
  renderItem,
  position = 'left',
  dateFormat = defaultDateFormat,
  ...props
}: VerticalTimelineProps) {
  return (
    <Timeline orientation="vertical" position={position} {...props}>
      {items.map((item, index) => (
        <TimelineItem key={item.id}>
          <TimelineSeparator>
            <TimelineDot
              status={item.status}
              icon={item.icon}
              color={item.color}
            />
            {index < items.length - 1 && <TimelineConnector active={item.status === 'completed'} />}
          </TimelineSeparator>
          <TimelineContent>
            {renderItem ? (
              renderItem(item, index)
            ) : (
              <>
                <h4 className="font-medium">{item.title}</h4>
                {item.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {item.description}
                  </p>
                )}
                {item.date && (
                  <time className="text-xs text-gray-500 dark:text-gray-400" dateTime={String(item.date)}>
                    {dateFormat(item.date)}
                  </time>
                )}
              </>
            )}
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
}

// ============================================================================
// HorizontalTimeline - Horizontal orientation timeline preset
// ============================================================================

export interface HorizontalTimelineProps extends Omit<TimelineProps, 'orientation' | 'position'> {
  items: TimelineItem[];
  renderItem?: (item: TimelineItem, index: number) => React.ReactNode;
  dateFormat?: (date: Date | string) => string;
}

export function HorizontalTimeline({
  items,
  renderItem,
  dateFormat = defaultDateFormat,
  ...props
}: HorizontalTimelineProps) {
  return (
    <Timeline orientation="horizontal" position="left" {...props}>
      {items.map((item, index) => (
        <TimelineItem key={item.id}>
          <TimelineSeparator>
            <TimelineDot
              status={item.status}
              icon={item.icon}
              color={item.color}
            />
            {index < items.length - 1 && <TimelineConnector active={item.status === 'completed'} />}
          </TimelineSeparator>
          <TimelineContent>
            {renderItem ? (
              renderItem(item, index)
            ) : (
              <>
                <h4 className="font-medium text-center text-sm">{item.title}</h4>
                {item.date && (
                  <time
                    className="text-xs text-gray-500 block text-center dark:text-gray-400"
                    dateTime={String(item.date)}
                  >
                    {dateFormat(item.date)}
                  </time>
                )}
              </>
            )}
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
}

// ============================================================================
// ProcessTimeline - Step-by-step process timeline
// ============================================================================

export interface ProcessStep {
  id: string;
  label: string;
  description?: string;
  status: 'completed' | 'current' | 'pending';
}

export interface ProcessTimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: ProcessStep[];
  orientation?: TimelineOrientation;
  size?: TimelineSize;
  onStepClick?: (step: ProcessStep) => void;
}

export function ProcessTimeline({
  steps,
  orientation = 'horizontal',
  size = 'md',
  onStepClick,
  className,
  ...props
}: ProcessTimelineProps) {
  const isHorizontal = orientation === 'horizontal';

  return (
    <div
      className={clsx(
        'process-timeline',
        isHorizontal ? 'flex flex-row items-center' : 'flex flex-col',
        className
      )}
      role="list"
      aria-label="Process steps"
      {...props}
    >
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div
            className={clsx(
              'process-step flex items-center gap-2',
              isHorizontal ? 'flex-col' : 'flex-row',
              onStepClick && 'cursor-pointer'
            )}
            onClick={() => onStepClick?.(step)}
            role="listitem"
            aria-current={step.status === 'current' ? 'step' : undefined}
          >
            <div
              className={clsx(
                'step-indicator rounded-full flex items-center justify-center font-medium',
                size === 'sm' && 'w-6 h-6 text-xs',
                size === 'md' && 'w-8 h-8 text-sm',
                size === 'lg' && 'w-10 h-10 text-base',
                step.status === 'completed' && 'bg-green-500 text-white',
                step.status === 'current' && 'bg-blue-500 text-white',
                step.status === 'pending' && 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              )}
            >
              {step.status === 'completed' ? '✓' : index + 1}
            </div>
            <div className={clsx('step-content', isHorizontal && 'text-center')}>
              <span
                className={clsx(
                  'block font-medium',
                  size === 'sm' && 'text-xs',
                  size === 'md' && 'text-sm',
                  size === 'lg' && 'text-base',
                  step.status === 'pending' && 'text-gray-500 dark:text-gray-400'
                )}
              >
                {step.label}
              </span>
              {step.description && (
                <span
                  className={clsx(
                    'block text-gray-500 dark:text-gray-400',
                    size === 'sm' && 'text-xs',
                    size === 'md' && 'text-xs',
                    size === 'lg' && 'text-sm'
                  )}
                >
                  {step.description}
                </span>
              )}
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={clsx(
                'step-connector',
                isHorizontal ? 'h-0.5 flex-1 mx-2' : 'w-0.5 h-8 ml-4',
                steps[index].status === 'completed'
                  ? 'bg-green-500'
                  : 'bg-gray-200 dark:bg-gray-700'
              )}
              aria-hidden="true"
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ============================================================================
// GitHistory - Git commit history style
// ============================================================================

export interface GitCommit {
  id: string;
  hash: string;
  message: string;
  author: {
    name: string;
    email?: string;
    avatar?: string;
  };
  date: Date | string;
  branch?: string;
  tags?: string[];
}

export interface GitHistoryProps extends React.HTMLAttributes<HTMLDivElement> {
  commits: GitCommit[];
  onCommitClick?: (commit: GitCommit) => void;
  dateFormat?: (date: Date | string) => string;
}

export function GitHistory({
  commits,
  onCommitClick,
  dateFormat = defaultDateFormat,
  className,
  ...props
}: GitHistoryProps) {
  return (
    <div
      className={clsx('git-history', className)}
      role="log"
      aria-label="Git commit history"
      {...props}
    >
      {commits.map((commit, index) => (
        <div
          key={commit.id}
          className={clsx(
            'git-commit flex items-start gap-3 py-3',
            index < commits.length - 1 && 'border-b border-gray-200 dark:border-gray-700',
            onCommitClick && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800'
          )}
          onClick={() => onCommitClick?.(commit)}
        >
          <div className="flex flex-col items-center">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            {index < commits.length - 1 && (
              <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                {commit.hash.substring(0, 7)}
              </code>
              {commit.branch && (
                <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-1.5 py-0.5 rounded">
                  {commit.branch}
                </span>
              )}
              {commit.tags?.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-1.5 py-0.5 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
            <p className="font-medium mt-1">{commit.message}</p>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
              {commit.author.avatar && (
                <img
                  src={commit.author.avatar}
                  alt={commit.author.name}
                  className="w-4 h-4 rounded-full"
                />
              )}
              <span>{commit.author.name}</span>
              <span>•</span>
              <time dateTime={String(commit.date)}>{dateFormat(commit.date)}</time>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// useTimeline Hook
// ============================================================================

export interface UseTimelineOptions<T extends { id: string }> {
  items: T[];
  initialActiveId?: string;
  loop?: boolean;
  onChange?: (item: T) => void;
}

export interface UseTimelineReturn<T extends { id: string }> {
  items: T[];
  activeId: string | null;
  activeItem: T | null;
  activeIndex: number;
  setActiveId: (id: string) => void;
  next: () => void;
  previous: () => void;
  first: () => void;
  last: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function useTimeline<T extends { id: string }>({
  items,
  initialActiveId,
  loop = false,
  onChange,
}: UseTimelineOptions<T>): UseTimelineReturn<T> {
  const [activeId, setActiveIdState] = React.useState<string | null>(
    initialActiveId || (items.length > 0 ? items[0].id : null)
  );

  const activeIndex = React.useMemo(
    () => items.findIndex((item) => item.id === activeId),
    [items, activeId]
  );

  const activeItem = React.useMemo(
    () => items.find((item) => item.id === activeId) || null,
    [items, activeId]
  );

  const setActiveId = React.useCallback(
    (id: string) => {
      setActiveIdState(id);
      const item = items.find((i) => i.id === id);
      if (item && onChange) {
        onChange(item);
      }
    },
    [items, onChange]
  );

  const next = React.useCallback(() => {
    if (items.length === 0) return;

    const currentIndex = activeIndex;
    let nextIndex = currentIndex + 1;

    if (nextIndex >= items.length) {
      if (loop) {
        nextIndex = 0;
      } else {
        return;
      }
    }

    setActiveId(items[nextIndex].id);
  }, [items, activeIndex, loop, setActiveId]);

  const previous = React.useCallback(() => {
    if (items.length === 0) return;

    const currentIndex = activeIndex;
    let prevIndex = currentIndex - 1;

    if (prevIndex < 0) {
      if (loop) {
        prevIndex = items.length - 1;
      } else {
        return;
      }
    }

    setActiveId(items[prevIndex].id);
  }, [items, activeIndex, loop, setActiveId]);

  const first = React.useCallback(() => {
    if (items.length > 0) {
      setActiveId(items[0].id);
    }
  }, [items, setActiveId]);

  const last = React.useCallback(() => {
    if (items.length > 0) {
      setActiveId(items[items.length - 1].id);
    }
  }, [items, setActiveId]);

  return {
    items,
    activeId,
    activeItem,
    activeIndex,
    setActiveId,
    next,
    previous,
    first,
    last,
    isFirst: activeIndex === 0,
    isLast: activeIndex === items.length - 1,
  };
}

// ============================================================================
// Utilities
// ============================================================================

function defaultDateFormat(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
